import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
//images
import { ru, pl } from "../img";
//styles and animations
import styled from "styled-components";
import { motion } from "framer-motion";
import { BurgerMenuAnimation } from "../styles/Animations";
//languages
import { useLanguage } from "../hooks/useLanguage";
import type { Language } from "../typings";
import { colors } from "../styles/variables";

interface NavProps {
  language: Language;
  languageHandler: (language: Language) => void;
}

interface DropdownItem {
  label: string;
  href: string;
}

const Nav = ({ language, languageHandler }: NavProps) => {
  const location = useLocation();
  // which dropdown is currently open: "services" | "knowledge" | "lang" | null
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [burgerOpen, setBurgerOpen] = useState(false);

  // open/close styling driven inline so it never depends on CSS specificity
  const dropdownStyle = (isOpen: boolean): React.CSSProperties => ({
    opacity: isOpen ? 1 : 0,
    visibility: isOpen ? "visible" : "hidden",
    pointerEvents: isOpen ? "auto" : "none",
    transform: `translateX(-50%) translateY(${isOpen ? "0" : "-0.5rem"})`,
  });

  //language text and image change
  const lang = useLanguage(language);
  let langImg;
  let langId: Language;
  let langTxt;
  if (language === "Polish") {
    langImg = pl;
    langId = "Polish";
    langTxt = lang.nav.Polish;
  } else {
    langImg = ru;
    langId = "Russian";
    langTxt = lang.nav.Russian;
  }

  const servicesItems: DropdownItem[] = [
    { label: lang.nav.servicesResidence, href: "/services#residence" },
    { label: lang.nav.servicesCitizenship, href: "/services#citizenship" },
    { label: lang.nav.servicesTranscription, href: "/services#transcription" },
    { label: lang.nav.servicesOther, href: "/services#other" },
  ];
  const knowledgeItems: DropdownItem[] = [
    { label: lang.nav.knowledgeLinks, href: "/faq#links" },
    { label: lang.nav.knowledgeFaqGeneral, href: "/faq#general" },
    { label: lang.nav.knowledgeFaqResidence, href: "/faq#residence" },
    { label: lang.nav.knowledgeFaqCitizenship, href: "/faq#citizenship" },
    { label: lang.nav.knowledgeFaqTranscription, href: "/faq#transcription" },
  ];

  useEffect(() => {
    setBurgerOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = burgerOpen ? "hidden" : "auto";
  }, [burgerOpen]);

  const renderDropdown = (
    id: string,
    href: string,
    label: string,
    items: DropdownItem[],
  ) => (
    <li
      className="dropdown"
      onMouseEnter={() => setOpenMenu(id)}
      onMouseLeave={() => setOpenMenu(null)}
      onFocus={() => setOpenMenu(id)}
      onBlur={() => setOpenMenu(null)}
    >
      <span
        className="dropdown-label"
        style={{
          color: openMenu === id ? colors.primary : colors.white,
          transform: openMenu === id ? "scale(1.2)" : "",
        }}
      >
        <Link to={href}>{label}</Link>
      </span>
      <HiddenList style={dropdownStyle(openMenu === id)}>
        <ul>
          {items.map((item) => (
            <li key={item.label}>
              <Link to={item.href}>{item.label}</Link>
            </li>
          ))}
        </ul>
      </HiddenList>
    </li>
  );

  if (window.innerWidth >= 770) {
    return (
      <StyledNav>
        <ul>
          <li>
            <Link to="/">{lang.nav.home}</Link>
          </li>
          {renderDropdown(
            "services",
            "/services",
            lang.nav.services,
            servicesItems,
          )}
          {renderDropdown(
            "knowledge",
            "/faq",
            lang.nav.knowledge,
            knowledgeItems,
          )}
          <li>
            <Link to="/contact">{lang.nav.contact}</Link>
          </li>
          <li
            className="dropdown lang"
            onMouseEnter={() => setOpenMenu("lang")}
            onMouseLeave={() => setOpenMenu(null)}
            onFocus={() => setOpenMenu("lang")}
            onBlur={() => setOpenMenu(null)}
          >
            <Link
              to={location.pathname}
              id={langId}
              onClick={(event) => {
                languageHandler(event.currentTarget.id as Language);
              }}
              style={{
                transform: openMenu === "lang" ? "scale(1.2)" : "",
                color: openMenu === "lang" ? colors.primary : colors.white,
              }}
            >
              <img src={langImg} alt={langId} />
              {langTxt}
            </Link>
            <HiddenList style={dropdownStyle(openMenu === "lang")}>
              <ul>
                <li>
                  <Link
                    to={location.pathname}
                    id={langId === "Polish" ? "Russian" : "Polish"}
                    onClick={(event) => {
                      languageHandler(event.currentTarget.id as Language);
                      setOpenMenu(null);
                    }}
                  >
                    <img src={langImg === pl ? ru : pl} alt="Polish" />
                    {langTxt === lang.nav.Polish
                      ? lang.nav.Russian
                      : lang.nav.Polish}
                  </Link>
                </li>
              </ul>
            </HiddenList>
          </li>
        </ul>
      </StyledNav>
    );
  } else {
    return (
      <StyledBurgerMenu>
        <button
          className={burgerOpen ? "trigger open" : "trigger"}
          onClick={() => {
            document.body.style.overflow = burgerOpen ? "auto" : "hidden";
            setBurgerOpen(!burgerOpen);
          }}
        >
          <span className="burger"></span>
          <span className="burger-text">{lang.nav.menu}</span>
        </button>
        <motion.nav
          variants={BurgerMenuAnimation}
          animate={burgerOpen ? "show" : "exit"}
        >
          <ul>
            <li>
              <Link to="/">{lang.nav.home}</Link>
            </li>
            <li>
              <Link to="/services" onClick={() => setBurgerOpen(false)}>
                {lang.nav.services}
              </Link>
            </li>
            <li>
              <Link to="/faq" onClick={() => setBurgerOpen(false)}>
                {lang.nav.knowledge}
              </Link>
            </li>
            <li>
              <Link to="/contact">{lang.nav.contact}</Link>
            </li>
            <li className="burg-lang">
              <Link
                to={location.pathname}
                id={langId === "Polish" ? "Russian" : "Polish"}
                onClick={(event) => {
                  languageHandler(event.currentTarget.id as Language);
                  setBurgerOpen(false);
                }}
              >
                {langTxt === lang.nav.Polish ? "RU" : "PL"}
              </Link>
            </li>
          </ul>
        </motion.nav>
      </StyledBurgerMenu>
    );
  }
};

//styles
const StyledNav = styled.nav`
  width: 1400px;
  height: 10vh;
  z-index: 10;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  ul {
    width: 70vw;
    list-style: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    li {
      text-align: center;
    }
    a {
      display: block;
      text-transform: uppercase;
      font-size: 1rem;
      letter-spacing: 0.1em;
      &:hover {
        transform: scale(1.2);
      }
    }
    .dropdown {
      position: relative;
    }
    .dropdown-label {
      display: block;
      text-transform: uppercase;
      font-size: 1rem;
      letter-spacing: 0.1em;
      cursor: pointer;
      color: ${colors.white};
      &:hover {
        transform: scale(1.2);
      }
    }
    .lang {
      a {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      img {
        width: 2rem;
        height: 2rem;
        margin-right: 0.5rem;
      }
    }
  }
  * {
    transition: all 0.3s;
  }
  @media (max-width: 1144px) {
    width: 95vw;
    padding: 0 1rem;
    ul {
      width: 100%;
    }
  }
`;

const HiddenList = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  padding-top: 0.5rem;
  min-width: 15rem;
  z-index: 20;
  /* opacity / visibility / transform are set inline from the open state */
  transition:
    opacity 0.25s ease,
    transform 0.25s ease,
    visibility 0.25s ease;
  ul {
    width: 100%;
    list-style: none;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    background-color: ${colors.white};
    border-top: 3px solid ${colors.primary};
    border-radius: 1rem;
    overflow: hidden;
    box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.15);
    li {
      width: 100%;
      padding: 1rem 1.5rem;
      white-space: nowrap;
      text-align: left;
      &:hover {
        background: rgba(117, 117, 117, 0.1);
        cursor: pointer;
        a {
          color: ${colors.primary};
        }
      }
    }
    a {
      display: block;
      color: ${colors.black};
      text-align: left;
      text-transform: none;
      letter-spacing: normal;
      font-size: 0.95rem;
      &:hover {
        transform: none;
      }
    }
  }
`;

const StyledBurgerMenu = styled(motion.div)`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  overflow: hidden;
  .trigger {
    position: fixed;
    top: 1rem;
    right: 1rem;
    z-index: 10;
    background: none;
    border: none;
    padding: 1rem 0 0 0;
    .burger {
      display: block;
      width: 3rem;
      height: 0.5rem;
      background: ${colors.dark};
      transition: all 0.1s;
      &:before {
        content: "";
        display: block;
        width: 3rem;
        height: 0.5rem;
        background: ${colors.dark};
        transition: all 0.5s;
        transform-origin: 0, 0;
        transform: translate(-0.5rem, -0.8rem) skew(15deg);
      }
      &:after {
        content: "";
        display: block;
        width: 3rem;
        height: 0.5rem;
        background: ${colors.dark};
        transition: all 0.5s;
        transform-origin: 0, 0;
        transform: translate(-0.5rem, 0.3rem) skew(15deg);
      }
    }
    .burger-text {
      display: block;
      font-size: 1rem;
      letter-spacing: 0.05em;
      margin-top: 1rem;
      text-transform: uppercase;
      font-weight: 500;
      text-align: center;
    }
  }
  .open {
    .burger {
      background: ${colors.primary};
      background: transparent;
      &:before {
        background: ${colors.primary};
        transform: rotate(225deg);
      }
      &:after {
        background: ${colors.primary};
        transform: translateY(-0.45rem) rotate(-225deg);
      }
    }
  }
  nav {
    opacity: 0;
    visibility: hidden;
    display: block;
    height: 100%;
    width: 100%;
    position: fixed;
    top: 0;
    right: 0;
    z-index: 5;
    text-align: center;
    background: ${colors.primary};
    ul {
      list-style: none;
      padding: 0;
      display: inline-flex;
      flex-direction: column;
      height: 100%;
      align-items: flex-end;
      justify-content: center;
      transform: translateX(-25%) skew(-15deg);
      li {
        display: block;
        margin: 0.5rem 0;
        text-align: right;
        transform: skew(16deg);
        max-width: 180px;
        a {
          display: block;
          padding: 0.8rem 0;
          color: ${colors.dark};
          font-size: 1.3rem;
          text-decoration: none;
          font-weight: 400;
          text-transform: uppercase;
          &:hover {
            color: ${colors.primary};
          }
        }
      }
      .burg-lang a {
        display: inline;
        color: ${colors.primary};
        &:first-of-type {
          margin-right: 1.5rem;
        }
      }
    }
    &:before {
      content: "";
      position: absolute;
      top: 0;
      left: 0rem;
      width: 100%;
      height: 100%;
      background: ${colors.white};
      transform-origin: 0 0;
      transform: skew(-14deg);
    }
  }
`;

export default Nav;
